import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import * as api from './api.ts';
import { AuthField, Button, Finding } from './ui.tsx';

/**
 * The OTP re-verify modal.
 *
 * `docs/security.md` R1 has a freshness gate on any mutation that moves
 * people — activating, standing down, closing, correcting, changing a
 * roster. It expires by wall clock in `ADMIN_FRESHNESS_SECONDS` (server-
 * side), and until this component existed the dashboard's answer to a
 * stale session was to display a sentence saying "sign out and back in,
 * then try again". That advice is:
 *   a) wrong at the moment somebody is mid-incident,
 *   b) demoralising for anyone reading it 30 seconds before they had
 *      planned to type a stand-down, and
 *   c) unnecessary — the same OTP flow that signs a person in also
 *      re-freshes an existing session.
 *
 * So: on `stale_session`, we open this modal, request a code, they type
 * six digits, `verifyCode` mints a fresh token pair, we invalidate `me`,
 * and the caller retries the action that was refused. The person never
 * leaves the screen they were on.
 *
 * The modal owns the code request end-to-end because two callers asking
 * for a code within seconds is not something to serve — the earlier code
 * is invalidated by the newer request and the operator ends up chasing
 * whichever email arrived first (`api/src/routes/auth.ts` handles this by
 * expiring older codes on request).
 */
export function ReverifyModal({
  email,
  onClose,
  onVerified,
}: {
  email: string;
  onClose: () => void;
  onVerified: () => void;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () => api.requestCode(email),
    onError: () =>
      setError('Could not send a fresh code. Check your connection and press "Send a new code".'),
  });
  // Fire exactly once when the modal opens. Rerunning on every render would
  // spam the endpoint and the mailbox.
  const [primed, setPrimed] = useState(false);
  useEffect(() => {
    if (primed) return;
    setPrimed(true);
    request.mutate();
  }, [primed, request]);

  const verify = useMutation({
    mutationFn: () => api.verifyCode(email, code),
    onSuccess: () => {
      // `me` reads `fresh` off the newly minted token; refetching it after
      // verify means the callers' `useQuery({queryKey:['me']})` observers
      // see the update on their next render.
      void qc.invalidateQueries({ queryKey: ['me'] });
      onVerified();
    },
    onError: () => setError('That code was not accepted. Request a new one.'),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm it is you"
      className="fixed inset-0 z-40 flex items-center justify-center bg-[rgba(14,21,36,0.45)] p-4"
      onClick={(e) => {
        // Click outside the card closes, so somebody hitting the button by
        // accident is not trapped. Escape still needs to be added if this
        // proves noisy; today the button and the click-out are enough.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[380px] rounded-[var(--radius-3)] bg-[var(--paper)] p-6 shadow-[0_22px_55px_-22px_rgba(14,21,36,0.4)]">
        <p className="text-[12px] leading-[16px] font-[650] tracking-[0.06em] text-[var(--ink-3)] uppercase">
          Confirm it is you
        </p>
        <h2 className="mt-2 text-[22px] leading-[26px] font-[650] tracking-[-0.006em] text-[var(--ink)]">
          Check your email
        </h2>
        <p className="mt-2 mb-5 text-[15px] leading-[20px] text-[var(--ink-2)]">
          Standing an event down or changing your people asks for one more check. We sent a
          six-digit code to <span className="text-[var(--ink)]">{email}</span>.
        </p>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length < 6 || verify.isPending) return;
            setError(null);
            verify.mutate();
          }}
        >
          <AuthField
            label="Six-digit code"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, ''))}
            inputMode="numeric"
            maxLength={6}
            autoFocus
            autoComplete="one-time-code"
            hint="It expires in ten minutes and works once."
          />

          <div className="pt-1">
            <Button
              type="submit"
              variant="primary"
              size="auth"
              full
              disabled={code.length < 6 || verify.isPending}
            >
              {verify.isPending ? 'Confirming…' : 'Confirm and continue'}
            </Button>
          </div>

          {error ? <Finding>{error}</Finding> : null}
        </form>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button
            variant="quiet"
            size="auth"
            disabled={request.isPending}
            onClick={() => {
              setError(null);
              setCode('');
              request.mutate();
            }}
          >
            Send a new code
          </Button>
          <Button variant="quiet" size="auth" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
